import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { getProblemTransitionVariants } from "../../utils/pageTransition";
import { useNavigate } from "react-router-dom";
import { Capacitor } from '@capacitor/core';
import { FaPencilAlt, FaRegLightbulb, FaRegFileAlt } from "react-icons/fa";
import { FiTarget, FiChevronRight } from "react-icons/fi";
import { AiOutlineArrowLeft } from "react-icons/ai";

// --- CUSTOM HOOKS & UTILS ---
import { useProblemSolver } from "../../hooks/useProblemSolver";
import { useOpenProblemFlow } from "../../hooks/useOpenProblemFlow";
import api from "../../api/apiHelper";
import { useAudio } from "../../hooks/useAudio";
import { useAiChat } from "../../hooks/useAiChat";
import { triggerHaptic } from "../../utils/haptics";
import { useAndroidSafeArea } from "../../hooks/useAndroidSafeArea";
import { RenderMarkdownWithLatex } from "../../utils/utils";
import { isNoAccessError } from "../../utils/access";

// --- COMPONENTS ---
import PageTestPage from "../molecules/PageTestPage";
import { readStoredStatus } from "../molecules/Page";
import DailyGoalSuccessPopup from "../molecules/DailyGoalSuccessPopup";
import AiChatPopup from "../molecules/AiChatPopup";
import SolutionStepper from "../molecules/SolutionStepper";
import HintAndFormulas from "../molecules/HintAndFormulas";
import ScratchpadModal from "../molecules/ScratchpadModal";
import HintPopup from "../molecules/HintPopup";
import SurefireInSessionPopup from "../molecules/SurefireInSessionPopup";
import SurefireBatchEndPopup from "../molecules/SurefireBatchEndPopup";
import { OpenProblemDecision, OpenProblemLadder, AskEagleButton } from "../molecules/OpenProblemFlow";
import { invalidateApiCache, writeApiCache, expireApiCache } from '../../utils/apiCache';

const buttonTransitionVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1, transition: { duration: 0.4, ease: "easeOut" } },
    exit: { opacity: 0, transition: { duration: 0.2 } }
};

const SolveNiepewniak = () => {
    const navigate = useNavigate();

    // --- CONTEXT NIEPEWNIAKA ---
    const [niepewniakInfo] = useState(() => {
        try { return JSON.parse(localStorage.getItem("currentNiepewniak")); } catch { return null; }
    });

    const platform = Capacitor.getPlatform();
    const isIOS = platform === 'ios';
    const prefersReducedMotion = useReducedMotion();
    const problemTransitionVariants = getProblemTransitionVariants(prefersReducedMotion);

    // --- LOGIKA HOOKA (ta sama co sesja nauki i pewniaki: ELO, streak, cel dzienny) ---
    const {
        currentProblem, selectedAnswerText, wasLastAnswerCorrect,
        isAnswerChecked, showSolution, isHintUsed, isBackupModeTriggered,
        solvedToday, dailyGoal,
        handleAnswerClick, handleSolution,
        handleHint, handleNextProblem, setShowSolution, shouldRequestReview,
        lastSubmissionResult, problemNumber, currentStreak, reinitialize,
    } = useProblemSolver({ isSurefireMode: true });

    const openFlow = useOpenProblemFlow({ currentProblem, handleAnswerClick, handleSolution });
    const { isOpen: isOpenProblem, openLadder } = openFlow;

    const playCorrectSound = useAudio('/audio/correct-answer.mp3');
    const {
        isChatOpen, chatHistory, isResponding, remainingCalls, maxCalls,
        handleOpenChat, handleSendMessage, setIsChatOpen, resetChat
    } = useAiChat(currentProblem, selectedAnswerText);

    const { paddingBottom } = useAndroidSafeArea();
    const stickyBottomStyle = isIOS ? '1rem' : '1.5rem';
    const fabBottomPosition = isIOS
        ? `calc(9rem + ${paddingBottom})`
        : `calc(8rem + ${paddingBottom})`;

    // --- STATE UI ---
    const [accountStatus, setAccountStatus] = useState(readStoredStatus);
    const [popupState, setPopupState] = useState({ isOpen: false, completionLevel: 0 });
    const [isBatchEndOpen, setIsBatchEndOpen] = useState(false);
    const [isBatchLoading, setIsBatchLoading] = useState(false);
    const [progressState, setProgressState] = useState({
        isOpen: false, message: "", progress: 0, learningTarget: 3, consolidationTarget: 2,
    });

    const prevSolvedCountRef = useRef(0);
    const [sketches, setSketches] = useState({});
    const [isScratchpadOpen, setIsScratchpadOpen] = useState(false);
    const [isHintPopupOpen, setIsHintPopupOpen] = useState(false);
    const [showBadgeTooltip, setShowBadgeTooltip] = useState(false);
    const badgeTooltipRef = useRef(null);

    const handleNiepewniakNext = useCallback(async () => {
        const isEnd = await handleNextProblem();
        if (isEnd) setIsBatchEndOpen(true);
    }, [handleNextProblem]);

    const handleContinueBatch = useCallback(async () => {
        setIsBatchLoading(true);
        try {
            const info = JSON.parse(localStorage.getItem("currentNiepewniak"));
            const res = await api.post(`niepewniaki/${info?.id}/start/`, {}, { skipLoading: true });
            const { final_problems, user_elo_dict } = res.data;
            localStorage.removeItem("problemsSession");
            localStorage.setItem("problemsData", JSON.stringify(final_problems));
            localStorage.setItem("userEloDict", JSON.stringify(user_elo_dict || {}));
            setIsBatchEndOpen(false);
            reinitialize();
        } catch (err) {
            console.error("Błąd ładowania nowej paczki:", err);
            // Dostęp do rozszerzenia mógł wygasnąć w trakcie sesji (402) — wracamy
            // na listę, gdzie paywall jest już obsłużony, zamiast zostawiać martwy przycisk.
            if (isNoAccessError(err)) navigate('/niepewniaki');
        } finally {
            setIsBatchLoading(false);
        }
    }, [reinitialize, navigate]);

    const handleBackToNiepewniaki = useCallback(() => {
        invalidateApiCache('user_dashboard_data', false);
        expireApiCache('niepewniaki');
        api.get('user_stats_view/', { skipLoading: true }).then(r => writeApiCache('user_stats', r.data)).catch(() => {});
        api.get('fields_preferences/', { skipLoading: true }).then(r => writeApiCache('fields_preferences', r.data)).catch(() => {});
        navigate('/niepewniaki');
    }, [navigate]);

    const handleLightbulbClick = () => {
        handleHint();
        setIsHintPopupOpen(true);
    };
    const handleFormulaClick = () => handleHint();
    const handleSaveSketch = (problemId, paths) => {
        setSketches(prev => ({ ...prev, [problemId]: paths }));
    };

    // --- EFEKTY ---

    // Postęp SRS niepewniaka wraca ze zwykłego solve_problem_attempt.
    useEffect(() => {
        if (isAnswerChecked && lastSubmissionResult) {
            const data = lastSubmissionResult.niepewniak_progress;
            if (data && Array.isArray(data)) {
                const completed = data.find(item => item.phase_completed === true);
                if (completed) {
                    const timer = setTimeout(() => {
                        setProgressState({
                            isOpen: true,
                            message: completed.completion_message || "Faza zaliczona!",
                            // Skala skumulowana 0..prog_opanowania — progi są per-niepewniak.
                            progress: completed.current_progress ?? 0,
                            learningTarget: completed.learning_target ?? 3,
                            consolidationTarget: completed.consolidation_target ?? 2,
                        });
                    }, 800);
                    return () => clearTimeout(timer);
                }
            }
        }
    }, [isAnswerChecked, lastSubmissionResult]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (badgeTooltipRef.current && !badgeTooltipRef.current.contains(e.target)) {
                setShowBadgeTooltip(false);
            }
        };
        if (showBadgeTooltip) document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [showBadgeTooltip]);

    useEffect(() => {
        if (isAnswerChecked) {
            if (wasLastAnswerCorrect) {
                playCorrectSound();
                triggerHaptic("success");
            } else {
                triggerHaptic("error");
            }
        }
    }, [isAnswerChecked, wasLastAnswerCorrect, playCorrectSound]);

    useEffect(() => {
        if (isAnswerChecked) {
            const savedStatus = localStorage.getItem("userStatus");
            if (savedStatus) setAccountStatus(JSON.parse(savedStatus));
        }
    }, [isAnswerChecked]);

    const dailyGoalProgress = dailyGoal > 0
        ? Math.min(100, (solvedToday / dailyGoal) * 100 + 5)
        : 0;

    useEffect(() => {
        if (isAnswerChecked && dailyGoal > 0) {
            const prevCompletionLevel = Math.floor(prevSolvedCountRef.current / dailyGoal);
            const currentCompletionLevel = Math.floor(solvedToday / dailyGoal);
            if (currentCompletionLevel > prevCompletionLevel && currentCompletionLevel <= 3) {
                setPopupState({ isOpen: true, completionLevel: currentCompletionLevel });
            }
        }
        prevSolvedCountRef.current = solvedToday;
    }, [isAnswerChecked, solvedToday, dailyGoal]);

    useEffect(() => {
        if (currentProblem) resetChat();
    }, [problemNumber, resetChat, currentProblem]);

    if (!currentProblem) {
        return (
            <PageTestPage showNav={false} accountStatus={accountStatus} dailyGoalProgress={dailyGoalProgress}>
                <div className="text-center py-10"><p className="text-xl text-gray-600">Ładowanie Niepewniaka...</p></div>
            </PageTestPage>
        );
    }

    return (
        <PageTestPage
            showNav={false}
            reportIssueData={{ problem_id: currentProblem.problem_id, field_id: currentProblem.field_id }}
            accountStatus={accountStatus}
            dailyGoalProgress={dailyGoalProgress}
        >
            {/* --- MODAL BRUDNOPISU --- */}
            <ScratchpadModal
                isOpen={isScratchpadOpen}
                onClose={() => setIsScratchpadOpen(false)}
                problem={currentProblem}
                initialPaths={sketches[currentProblem.problem_id]}
                onSave={handleSaveSketch}
            />

            {/* --- FAB (PRZYCISK BRUDNOPISU) --- */}
            <button
                onClick={() => setIsScratchpadOpen(true)}
                className="fixed right-4 z-40 bg-[#233855] text-white p-4 rounded-full shadow-[0_4px_14px_0_rgba(0,0,0,0.39)] hover:bg-[#345081] transition-transform hover:scale-105 active:scale-95 border-2 border-[#E09F2E]"
                style={{ bottom: fabBottomPosition }}
                aria-label="Otwórz brudnopis"
            >
                <FaPencilAlt size={20} />
            </button>

            {/* --- POPUP CELU DZIENNEGO (PRIORYTET) --- */}
            {popupState.isOpen && (
                <DailyGoalSuccessPopup
                    completionLevel={popupState.completionLevel}
                    onClose={() => setPopupState({ isOpen: false, completionLevel: 0 })}
                    isFirstSession={accountStatus?.current_streak === 0}
                    shouldRequestReview={shouldRequestReview}
                    streakDay={currentStreak}
                />
            )}

            {/* --- POPUP KONIEC PACZKI --- */}
            <SurefireBatchEndPopup
                isOpen={isBatchEndOpen}
                atomName={niepewniakInfo?.title}
                unitLabel="Niepewniaka"
                onContinue={handleContinueBatch}
                onFinish={handleBackToNiepewniaki}
                isLoading={isBatchLoading}
            />

            {/* --- POPUP POSTĘPU SRS --- */}
            <SurefireInSessionPopup
                isOpen={progressState.isOpen && !popupState.isOpen}
                message={progressState.message}
                progress={progressState.progress}
                atomName={niepewniakInfo?.title}
                unit="niepewniak"
                panelLabel="Przejdź do panelu niepewniaków →"
                learningTarget={progressState.learningTarget}
                consolidationTarget={progressState.consolidationTarget}
                onContinue={() => setProgressState(prev => ({ ...prev, isOpen: false }))}
                onGoToPanel={() => {
                    setProgressState(prev => ({ ...prev, isOpen: false }));
                    handleBackToNiepewniaki();
                }}
            />

            {/* --- GŁÓWNA TREŚĆ --- */}
            <div className="flex flex-col max-w-2xl mx-auto w-full flex-grow px-4 lg:px-0">

                <div className="w-full flex mt-[3vh] lg:mt-[8vh] mb-2">
                    <button
                        onClick={handleBackToNiepewniaki}
                        className="text-gray-500 hover:text-gray-800 transition-colors flex items-center gap-1 text-sm font-medium"
                    >
                        <AiOutlineArrowLeft /> Wróć do listy niepewniaków
                    </button>
                </div>

                <div className="relative w-full mt-4">
                <AnimatePresence mode="wait" initial={false}>
                <motion.div
                    key={currentProblem.problem_id}
                    variants={problemTransitionVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    className="w-full"
                >
                {/* --- BIAŁA KARTA (TREŚĆ ZADANIA) --- */}
                <div className="bg-white rounded-xl lg:rounded-[3rem] shadow-[4px_4px_12px_rgba(0,0,0,0.30)] border border-gray-200 py-6 px-5 lg:px-14 w-full overflow-hidden relative">

                    {/* --- BADGE NIEPEWNIAKA --- */}
                    {niepewniakInfo && (
                        <div
                            ref={badgeTooltipRef}
                            onClick={() => setShowBadgeTooltip(!showBadgeTooltip)}
                            className="absolute top-0 left-0 z-30 cursor-pointer group"
                        >
                            <div className="px-3 py-1 rounded-br-2xl border-b border-r flex items-center gap-1.5 transition-opacity group-hover:opacity-100 bg-gray-100 border-gray-200 text-gray-500 lg:pl-8 lg:pr-6 lg:py-2">
                                <FiTarget size={11} className="opacity-60" />
                                <span className="text-[8px] font-bold font-apotek tracking-[0.15em] uppercase leading-none pt-0.5">
                                    Niepewniak
                                </span>
                            </div>
                            <AnimatePresence>
                                {showBadgeTooltip && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -5 }}
                                        animate={{ opacity: 1, y: 0, transition: { duration: 0.2, ease: [0.22, 1, 0.36, 1] } }}
                                        exit={{ opacity: 0, transition: { duration: 0.1 } }}
                                        className="absolute top-full left-0 mt-2 w-64 bg-white border border-gray-200 shadow-xl rounded-lg p-3 z-50 text-left"
                                    >
                                        <p className="text-[10px] font-bold text-gray-800 uppercase tracking-wider mb-1 font-apotek">
                                            Niepewniak rozszerzenia
                                        </p>
                                        <p className="text-xs text-gray-700 leading-relaxed font-normal">
                                            Typy zadań otwartych, które najczęściej decydują o wyniku na rozszerzeniu.
                                        </p>
                                        <div className="absolute -top-[5px] left-4 w-2 h-2 bg-white border-t border-l border-gray-200 transform rotate-45"></div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    )}

                    {/* --- IKONA ŻARÓWKI --- */}
                    {currentProblem.hint && currentProblem.hint.trim() !== "" && (
                        <button
                            onClick={handleLightbulbClick}
                            className="absolute top-4 right-4 text-[#E09F2E] hover:text-[#b88226] hover:scale-110 transition-all p-2 z-10"
                            aria-label="Zobacz wskazówkę"
                        >
                            <FaRegLightbulb size={24} strokeWidth={1} className="drop-shadow-sm" />
                        </button>
                    )}

                    <div className="w-full space-y-6">
                        {/* Header Zadania */}
                        <div className="w-full flex justify-center items-center text-center mb-2 lg:mt-[0.5vh]">
                            <span className="text-[#233855] font-apotek text-xs lg:xs tracking-widest uppercase font-normal">
                                {currentProblem.niepewniak_name || niepewniakInfo?.title || "NIEPEWNIAK"}
                            </span>
                        </div>

                        {/* Pytanie */}
                        <p className="text-base lg:text-normal font-normal text-gray-900 text-center min-h-[50px] flex items-center justify-center lg:mt-[0.5vh]">
                            <RenderMarkdownWithLatex text={!currentProblem.is_mobile && currentProblem.points_assigned ? `(${currentProblem.points_assigned} pkt) ${currentProblem.question}` : currentProblem.question} />
                        </p>

                        {/* Obrazek */}
                        {currentProblem.image_link && (
                            <div className="flex justify-center py-2">
                                <img
                                    src={
                                        currentProblem.image_link.startsWith('http')
                                            ? currentProblem.image_link
                                            : `${process.env.REACT_APP_STATIC_FILES_SOURCE || ''}/images/${currentProblem.image_link}`
                                    }
                                    alt="Ilustracja zadania"
                                    className="max-w-full h-auto rounded border"
                                />
                            </div>
                        )}

                        {/* KARTA WZORÓW / HINTY */}
                        <div className="w-full">
                            {(() => {
                                const hasLinkedFormulas = currentProblem.linked_formulas && currentProblem.linked_formulas.length > 0;
                                if (!hasLinkedFormulas) return null;
                                if (isHintUsed) {
                                    return (
                                        <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                                            <HintAndFormulas hintText={null} formulas={currentProblem.linked_formulas} />
                                        </div>
                                    );
                                }
                                return (
                                    <button
                                        onClick={handleFormulaClick}
                                        className="group w-full flex items-center gap-3 bg-white border border-[#E09F2E]/40 px-4 py-3 rounded-xl shadow-sm hover:shadow-md hover:border-[#E09F2E] transition-all active:scale-[0.98] mb-2"
                                    >
                                        <div className="w-9 h-9 rounded-lg bg-[#233855] flex items-center justify-center flex-shrink-0">
                                            <FaRegFileAlt size={16} className="text-[#E09F2E]" />
                                        </div>
                                        <div className="flex-1 text-left min-w-0">
                                            <span className="block text-sm font-bold text-[#233855] font-apotek leading-tight">Karta Wzorów</span>
                                            <span className="block text-xs text-gray-400 leading-tight">Otwórz wzory do zadania</span>
                                        </div>
                                        <div className="w-7 h-7 rounded-full bg-gray-100 group-hover:bg-[#233855]/10 flex items-center justify-center flex-shrink-0 transition-colors">
                                            <FiChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#233855] group-hover:translate-x-0.5 transition-all" />
                                        </div>
                                    </button>
                                );
                            })()}
                        </div>

                        {/* SAMOOCENA (zadania otwarte — cały content rozszerzenia) */}
                        <OpenProblemDecision
                            currentProblem={currentProblem}
                            flow={openFlow}
                            isAnswerChecked={isAnswerChecked}
                            wasLastAnswerCorrect={wasLastAnswerCorrect}
                            showSolution={showSolution}
                            setShowSolution={setShowSolution}
                        />
                    </div>
                </div>
                {/* --- /BIAŁA KARTA --- */}

                {/* POZA KARTĄ: rozwiązanie krok po kroku + Orzeł AI */}
                {showSolution && !openLadder && (
                    <div className="w-full mt-4 space-y-3 animate-in fade-in zoom-in duration-300">
                        <SolutionStepper solutionText={currentProblem.solution || "Brak rozwiązania."} />
                        <AskEagleButton onClick={handleOpenChat} disabled={isResponding} />
                    </div>
                )}

                {/* POZA KARTĄ ("Mam inaczej"): pełne rozwiązanie + decyzja finalna */}
                {isOpenProblem && openLadder && (
                    <OpenProblemLadder
                        currentProblem={currentProblem}
                        flow={openFlow}
                        isAnswerChecked={isAnswerChecked}
                        isBackupModeTriggered={isBackupModeTriggered}
                        onOpenChat={handleOpenChat}
                        isResponding={isResponding}
                        showBackupWidget={false}
                    />
                )}

                {/* PRZYCISK KOLEJNE ZADANIE */}
                <AnimatePresence>
                    {isAnswerChecked && (
                        <motion.div
                            key="next-button"
                            variants={buttonTransitionVariants}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                            className="w-full pt-6 flex justify-center"
                        >
                            <button
                                onClick={handleNiepewniakNext}
                                className="flex w-full lg:w-[25rem] justify-center rounded-lg bg-[#233855] px-3 py-2 text-xs lg:text-xs font-apotek tracking-widest text-slate-100 shadow-lg hover:bg-[#345081] focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600"
                            >
                                KOLEJNE ZADANIE
                            </button>
                        </motion.div>
                    )}
                </AnimatePresence>
                </motion.div>
                </AnimatePresence>
                </div>
                <div className="h-[10vh] flex-shrink-0" />
            </div>

            <div className="flex-grow" />
            <div className="sticky w-full pt-3 z-30 lg:bottom-[5vh]" style={{ bottom: stickyBottomStyle }} />

            {/* --- POPUPY (AI, HINT) --- */}
            <AiChatPopup
                isOpen={isChatOpen}
                onClose={() => setIsChatOpen(false)}
                chatHistory={chatHistory}
                onSendMessage={handleSendMessage}
                isResponding={isResponding}
                problemQuestion={currentProblem?.question}
                remainingCalls={remainingCalls}
                maxCalls={maxCalls}
            />
            <HintPopup
                isOpen={isHintPopupOpen}
                onClose={() => setIsHintPopupOpen(false)}
                hintText={currentProblem.hint}
            />
        </PageTestPage>
    );
};

export default SolveNiepewniak;
